import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  CheckCircle2, 
  Loader2, 
  Play, 
  ExternalLink,
  Star,
  Landmark,
  Calendar,
  FileCheck,
  UserPlus,
  Lightbulb,
  Circle,
  FileText,
  Building,
  UserCheck
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WorkflowLaunchWizard } from "@/components/workflows/WorkflowLaunchWizard";
import { WorkflowPhaseManager } from "@/components/tasks/WorkflowPhaseManager";
import { Progress } from "@/components/ui/progress";
import { WorkflowInstanceDetailModal } from "@/components/workflows/WorkflowInstanceDetailModal";
import { VideoPlayer } from "@/components/journey/VideoPlayer";

interface JourneyWorkflowStepProps {
  stepId: string;
  userId: string;
  workflowKey: string;
  isCompleted: boolean;
  actionData?: Record<string, any>;
  actionConfig?: Record<string, any>;
  onComplete: () => void;
}

type WorkflowState = 'not_started' | 'launching' | 'in_progress' | 'completed';

// Icon mapping for resolution items
const RESOLUTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'landmark': Landmark,
  'calendar': Calendar,
  'file-check': FileCheck,
  'user-plus': UserPlus,
  'building': Building,
  'user-check': UserCheck,
};

interface ResolutionItemProps {
  title: string;
  description: string;
  icon?: string;
  tip?: string;
  isOptional?: boolean;
}

function ResolutionItem({ title, description, icon, tip, isOptional }: ResolutionItemProps) {
  const IconComponent = icon ? RESOLUTION_ICONS[icon] : Circle;
  
  return (
    <div className="flex gap-3 p-3 rounded-lg bg-background/50">
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isOptional ? 'bg-blue-100 text-blue-600' : 'bg-primary/10 text-primary'}`}>
        {IconComponent && <IconComponent className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <h5 className="font-medium text-sm">{title}</h5>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {tip && (
          <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{tip}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function JourneyWorkflowStep({
  stepId,
  userId,
  workflowKey,
  isCompleted,
  actionData,
  actionConfig,
  onComplete,
}: JourneyWorkflowStepProps) {
  const [workflowState, setWorkflowState] = useState<WorkflowState>(
    isCompleted ? 'completed' : 
    actionData?.task_id ? 'in_progress' : 'not_started'
  );
  const [taskId, setTaskId] = useState<string | null>(actionData?.task_id || null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [instanceModalOpen, setInstanceModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Video progress state
  const [videoWatchedPercent, setVideoWatchedPercent] = useState(actionData?.video_watch_percent || 0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const watchedPercentRef = useRef(actionData?.video_watch_percent || 0);
  const lastSavedPercentRef = useRef(Math.floor((actionData?.video_watch_percent || 0) / 5));
  const isCompletedRef = useRef(false);

  // Extract video config from actionConfig
  const videoUrl = actionConfig?.video_url;
  const videoRequiredWatchPercent = actionConfig?.video_required_watch_percent || 95;

  // Extract special meeting config
  const isSpecialMeeting = actionConfig?.is_special_meeting || false;
  const specialMeetingBanner = actionConfig?.special_meeting_banner;
  const instructionsIntro = actionConfig?.instructions_intro;
  const requiredResolutions = actionConfig?.required_resolutions || [];
  const optionalResolutions = actionConfig?.optional_resolutions || [];
  const additionalResolutionsNote = actionConfig?.additional_resolutions_note;
  const postMeetingNote = actionConfig?.post_meeting_note;

  // Fetch workflow template
  const { data: workflow } = useQuery({
    queryKey: ['workflow-template', workflowKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('workflow_key', workflowKey)
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch user's UNA
  const { data: una } = useQuery({
    queryKey: ['user-una', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unas')
        .select('id, name, treasury_secretary_id, primary_officer_role')
        .eq('treasury_secretary_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch user profile for Secretary Treasurer name
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: isSpecialMeeting,
  });

  // Fetch journey form data (for place of worship address from Step 7)
  const { data: journeyFormData } = useQuery({
    queryKey: ['journey-form-data', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_journey_state')
        .select('journey_form_data')
        .eq('user_id', userId)
        .single();
      
      if (error) throw error;
      return data?.journey_form_data as Record<string, any> | null;
    },
    enabled: isSpecialMeeting,
  });

  // Fetch task if in progress
  const { data: task, refetch: refetchTask } = useQuery({
    queryKey: ['journey-workflow-task', taskId],
    queryFn: async () => {
      if (!taskId) return null;
      const { data, error } = await supabase
        .from('custom_tasks')
        .select('*')
        .eq('id', taskId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!taskId,
  });

  // Check task status for completion
  useEffect(() => {
    if (task?.status === 'completed' && workflowState !== 'completed') {
      handleWorkflowComplete();
    }
  }, [task?.status]);

  // Refresh task periodically when in progress
  useEffect(() => {
    if (workflowState === 'in_progress' && taskId) {
      const interval = setInterval(() => {
        refetchTask();
      }, 5000); // Check every 5 seconds
      return () => clearInterval(interval);
    }
  }, [workflowState, taskId]);

  // Check if video was already completed
  useEffect(() => {
    if (videoUrl && videoWatchedPercent >= videoRequiredWatchPercent) {
      setVideoCompleted(true);
    }
  }, [videoUrl, videoWatchedPercent, videoRequiredWatchPercent]);

  // Save video progress on unmount
  useEffect(() => {
    return () => {
      if (isCompletedRef.current) return;
      
      const currentProgress = watchedPercentRef.current;
      if (currentProgress > 0 && currentProgress > (actionData?.video_watch_percent || 0)) {
        supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: stepId,
            status: 'active',
            action_data: { 
              ...actionData,
              video_watch_percent: currentProgress 
            },
          }, { onConflict: 'user_id,step_id' })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
          });
      }
    };
  }, [userId, stepId, actionData, queryClient]);

  // Video progress handler - save every 5%
  const handleVideoProgress = useCallback(async (percent: number) => {
    watchedPercentRef.current = percent;
    setVideoWatchedPercent(percent);
    
    const currentInterval = Math.floor(percent / 5);
    if (currentInterval > lastSavedPercentRef.current) {
      lastSavedPercentRef.current = currentInterval;
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { 
            ...actionData,
            video_watch_percent: percent 
          },
        }, { onConflict: 'user_id,step_id' });
      
      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
    }
  }, [userId, stepId, actionData, queryClient]);

  // Video completion handler
  const handleVideoComplete = useCallback(() => {
    setVideoCompleted(true);
  }, []);

  const handleStartWorkflow = () => {
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    // Check if a task was created
    checkForNewTask();
  };

  const checkForNewTask = async () => {
    if (!una?.id || !workflow?.id) return;

    // Look for a recently created task for this workflow
    const { data: recentTask } = await supabase
      .from('custom_tasks')
      .select('id, status, workflow_completion_percentage')
      .eq('workflow_id', workflow.id)
      .eq('una_id', una.id)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (recentTask && recentTask.id !== taskId) {
      setTaskId(recentTask.id);
      setWorkflowState('in_progress');

      // Save task ID to progress
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { task_id: recentTask.id },
        }, { onConflict: 'user_id,step_id' });
    }
  };

  const handleWorkflowComplete = async () => {
    setWorkflowState('completed');

    try {
      // Mark step as completed
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { task_id: taskId },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        } else {
          // No more steps - mark journey complete
          await supabase
            .from('user_journey_state')
            .update({
              journey_completed: true,
              journey_completed_at: new Date().toISOString(),
            })
            .eq('user_id', userId);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
      onComplete();
    } catch (error) {
      console.error('Error completing workflow step:', error);
    }
  };

  // Check if first meeting data is ready
  const isFirstMeetingDataReady = !isSpecialMeeting || (!!userProfile && !!una);

  // Build pre-populated data for special meeting workflow (first meeting only)
  const firstMeetingInitialData = useMemo(() => {
    if (!isSpecialMeeting) return undefined;
    // Don't return undefined here - we'll gate the button instead
    // This ensures data is ready when useMemo computes

    // Build meeting purpose from required resolutions
    const resolutionTitles = requiredResolutions.map((res: any) => res.title);
    const meetingPurpose = resolutionTitles.length > 0 
      ? `Discuss and pass: ${resolutionTitles.join('; ')}`
      : 'First official UNA meeting to pass foundational resolutions';

    // Get place of worship address from journey form data (Step 7)
    const placeOfWorship = journeyFormData?.place_of_worship;
    const meetingLocation = placeOfWorship 
      ? `${placeOfWorship.address || ''}${placeOfWorship.city_state_zip ? ', ' + placeOfWorship.city_state_zip : ''}`
      : '';

    // Primary officer (Secretary Treasurer, President, etc.) as attendee
    const primaryOfficerRole = una?.primary_officer_role || 'Secretary Treasurer';
    const primaryOfficerAttendee = {
      id: userId,
      name: userProfile?.full_name || primaryOfficerRole,
      role: primaryOfficerRole,
    };

    return {
      meeting_type: 'Special Meeting',
      meeting_purpose: meetingPurpose,
      meeting_date: new Date().toISOString().split('T')[0], // Today
      meeting_time: '14:00', // 2 PM
      meeting_duration: '30 minutes',
      meeting_location: meetingLocation.trim() || '', // User must fill if empty
      meeting_format: 'In-Person',
      meeting_conductor_id: userId, // Primary officer
      meeting_conductor_name: userProfile?.full_name || primaryOfficerRole,
      recording_officer_id: userId, // Primary officer
      required_attendees: [primaryOfficerAttendee],
      optional_attendees: [],
      external_guests: [],
    };
  }, [isSpecialMeeting, userProfile, requiredResolutions, journeyFormData, userId, una?.primary_officer_role]);

  if (isCompleted || workflowState === 'completed') {
    return (
      <div className="space-y-4">
        {/* Show video for rewatching even when completed */}
        {videoUrl && (
          <Card>
            <CardContent className="pt-6">
              <VideoPlayer
                videoUrl={videoUrl}
                requiredWatchPercent={0}
                onProgress={() => {}}
                onComplete={() => {}}
                initialProgress={100}
                hideProgressUI
              />
            </CardContent>
          </Card>
        )}
        <div className="flex items-center justify-center py-4 text-green-600">
          <CheckCircle2 className="w-6 h-6 mr-2" />
          <span className="font-medium">Workflow completed</span>
        </div>
      </div>
    );
  }

  if (workflowState === 'not_started') {
    // Determine if button should be disabled
    const buttonDisabled = (isSpecialMeeting && !isFirstMeetingDataReady) || (videoUrl && !videoCompleted);
    
    return (
      <div className="space-y-4">
        {/* Video Section - Show if video_url exists and video not completed */}
        {videoUrl && !videoCompleted && (
          <Card>
            <CardContent className="pt-6">
              <VideoPlayer
                videoUrl={videoUrl}
                requiredWatchPercent={videoRequiredWatchPercent}
                onProgress={handleVideoProgress}
                onComplete={handleVideoComplete}
                initialProgress={videoWatchedPercent}
              />
            </CardContent>
          </Card>
        )}

        {/* Video Completed Indicator */}
        {videoUrl && videoCompleted && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">Video completed</span>
          </div>
        )}

        {/* Special Meeting Banner & Instructions */}
        {isSpecialMeeting && (
          <div className="space-y-4">
            {/* Banner */}
            {specialMeetingBanner && (
              <div className="flex items-center gap-2 p-3 bg-amber-100 border border-amber-300 rounded-lg">
                <Star className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span className="font-semibold text-amber-800">{specialMeetingBanner}</span>
              </div>
            )}

            {/* Intro Text */}
            {instructionsIntro && (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {instructionsIntro}
              </p>
            )}

            {/* Required Resolutions */}
            {requiredResolutions.length > 0 && (
              <Card className="border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold text-sm">Resolutions to Discuss & Pass</h4>
                  </div>
                  <div className="space-y-2">
                    {requiredResolutions.map((res: any, idx: number) => (
                      <ResolutionItem
                        key={idx}
                        title={res.title}
                        description={res.description}
                        icon={res.icon}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Optional Resolutions */}
            {optionalResolutions.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-sm text-blue-800">Optional: Strengthen Your UNA</h4>
                  </div>
                  <div className="space-y-2">
                    {optionalResolutions.map((res: any, idx: number) => (
                      <ResolutionItem
                        key={idx}
                        title={res.title}
                        description={res.description}
                        icon={res.icon}
                        tip={res.tip}
                        isOptional
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Resolutions Note */}
            {additionalResolutionsNote && (
              <p className="text-sm text-muted-foreground italic px-1">
                {additionalResolutionsNote}
              </p>
            )}
          </div>
        )}

        {/* Standard Workflow Info */}
        {workflow && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">{workflow.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {workflow.description}
            </p>
          </div>
        )}

        {/* Post Meeting Note */}
        {postMeetingNote && isSpecialMeeting && (
          <div className="text-xs text-muted-foreground bg-green-50 border border-green-200 rounded-lg p-3">
            <span className="font-medium text-green-700">After your meeting:</span>{' '}
            <span className="text-green-600">{postMeetingNote}</span>
          </div>
        )}

        <Button 
          onClick={handleStartWorkflow} 
          size="lg" 
          className="w-full sm:w-auto"
          disabled={buttonDisabled}
        >
          {isSpecialMeeting && !isFirstMeetingDataReady ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Preparing meeting defaults...
            </>
          ) : videoUrl && !videoCompleted ? (
            <>
              <Play className="w-4 h-4 mr-2" />
              Watch Video to Continue
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              {isSpecialMeeting ? 'Begin Special Meeting Workflow' : 'Begin Workflow'}
            </>
          )}
        </Button>

        {/* Workflow Launch Wizard Dialog */}
        {workflow && (
          <WorkflowLaunchWizard
            workflow={workflow}
            open={wizardOpen}
            onOpenChange={handleWizardClose}
            skipNavigation={true}
            suggestedMotions={isSpecialMeeting ? [
              ...requiredResolutions.map((res: any) => ({
                motion_text: res.title,
                description: res.description,
                is_required: true
              })),
              ...optionalResolutions.map((res: any) => ({
                motion_text: res.title,
                description: res.description,
                is_required: false
              }))
            ] : undefined}
            initialWorkflowData={firstMeetingInitialData}
          />
        )}
      </div>
    );
  }

  if (workflowState === 'in_progress' && task) {
    const completionPercent = task.workflow_completion_percentage || 0;

    // Create workflowInstance object for the modal
    const workflowInstance = {
      task_id: task.id,
      workflow_id: task.workflow_id,
      status: task.status,
      progress_percentage: completionPercent,
      una_id: una?.id,
    };

    return (
      <div className="space-y-4">
        {/* Progress Indicator */}
        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Workflow Progress</span>
              <span className="text-sm text-muted-foreground">{completionPercent}%</span>
            </div>
            <Progress value={completionPercent} className="h-2" />
          </div>
        </div>

        {/* Open Workflow Button */}
        <Button onClick={() => setInstanceModalOpen(true)} className="w-full">
          <ExternalLink className="w-4 h-4 mr-2" />
          Continue Workflow
        </Button>

        {/* Workflow Instance Modal */}
        {workflow && una && (
          <WorkflowInstanceDetailModal
            open={instanceModalOpen}
            onOpenChange={(open) => {
              setInstanceModalOpen(open);
              if (!open) {
                refetchTask();
              }
            }}
            workflowInstance={workflowInstance}
            onUpdate={() => refetchTask()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin mr-2" />
      <span>Loading...</span>
    </div>
  );
}
